import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import DataTable from 'react-data-table-component'
import { ArrowLeftIcon } from '@heroicons/react/20/solid'
import { ArrowDownTrayIcon, ArrowPathIcon } from '@heroicons/react/16/solid'
import { Select } from 'components/select'
import { MultiSelect } from 'components/multi-select'
import { Input } from 'components/input'
import { Divider } from 'components/divider'
import loadingImage from '../../assets/images/boat-loading.gif'
import Flatpickr from 'react-flatpickr'
import { useLazyQuery } from '@apollo/client'
import { GET_FLEET_UTILIZATION_REPORT, GET_MASTER_BOAT_ROUTES } from 'graphql/reports/queries'
import { SEARCH_BOAT_LIST } from 'graphql/boat/queries'
import moment from 'moment'
import { ErrorDialog } from 'components/custom/ResponseDialog'
import i18n from 'i18next'
import { BoatIcon } from 'components/icons'

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50]

const INITIAL_FILTER_STATE = {
    trip_date: {
        start_date: moment().subtract(6, 'months').format('YYYY-MM-DD'),
        end_date: moment().format('YYYY-MM-DD'),
    },
    route_ids: [],
    boat_ids: [],
    search_string: '',
}

export default function FleetUtilizationReport() {
    const navigate = useNavigate()
    const { t } = useTranslation()

    const [filterObj, setFilterObj] = useState(INITIAL_FILTER_STATE)
    const [data, setData] = useState([])
    const [totalRows, setTotalRows] = useState(0)
    const [perPage, setPerPage] = useState(10)
    const [currentPage, setCurrentPage] = useState(1)
    const [exportLoading, setExportLoading] = useState(false)
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [dialogMessage, setDialogMessage] = useState('')
    const [routeOptions, setRouteOptions] = useState([])
    const [boatOptions, setBoatOptions] = useState([])
    const [filterApplied, setFilterApplied] = useState(false)

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            setData([])
            setFilterApplied(false)
        }
    }, [])

    const [getFleetUtilizationReport, { loading: reportLoading }] = useLazyQuery(GET_FLEET_UTILIZATION_REPORT, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.fleetUtilizationReport.success && data.fleetUtilizationReport.code === '200') {
                setData(data.fleetUtilizationReport.fleet_utilization_report.map(formatFleetData))
                setTotalRows(data.fleetUtilizationReport.total_count)
            } else {
                handleError(data.fleetUtilizationReport.message)
            }
        },
        onError: error => handleError(error.message),
    })

    const [getMasterBoatRoutes] = useLazyQuery(GET_MASTER_BOAT_ROUTES, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.getMasterBoatRoute.success && data.getMasterBoatRoute.code === '200') {
                setRouteOptions(
                    data.getMasterBoatRoute.master_boat_routes.map(route => ({
                        value: route.master_route_id,
                        label: route.route_name.en || route.route_name.ar,
                    }))
                )
            }
        },
    })

    const [searchBoats] = useLazyQuery(SEARCH_BOAT_LIST, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.searchBoat.success && data.searchBoat.code === '200') {
                setBoatOptions(
                    data.searchBoat.boats.map(boat => ({
                        value: boat.id,
                        label: boat.name.en || boat.name.ar,
                    }))
                )
            }
        },
    })

    const [getFleetUtilizationReportForExport] = useLazyQuery(GET_FLEET_UTILIZATION_REPORT, {
        fetchPolicy: 'network-only',
        onError: error => {
            handleError(error.message)
            setExportLoading(false)
        },
    })

    const formatFleetData = fleet => ({
        boatId: fleet.boat_id,
        boatName: fleet.boat_name?.en ? fleet.boat_name.en : fleet.boat_name.ar,
        regNumber: fleet.reg_number,
        totalTrips: fleet.total_trips,
        operationalHours: fleet.operational_hours,
        idealTime: fleet.ideal_time,
    })

    const handleError = message => {
        setDialogMessage(message || t('Failed to fetch fleet utilization report'))
        setShowErrDialog(true)
    }

    const fetchFleetUtilizationReport = (page, filterData = filterObj, limit = perPage) => {
        const offset = (page - 1) * limit
        getFleetUtilizationReport({
            variables: {
                page: {
                    limit,
                    offset,
                },
                fleetFilter: {
                    trip_date: {
                        start_date: filterData.trip_date.start_date,
                        end_date: filterData.trip_date.end_date,
                    },
                    route_ids: filterData.route_ids?.length > 0 ? filterData.route_ids : null,
                    boat_ids: filterData.boat_ids?.length > 0 ? filterData.boat_ids : null,
                },
            },
        })
    }

    useEffect(() => {
        getMasterBoatRoutes({
            variables: {
                page: {
                    offset: 0,
                    limit: 100,
                },
            },
        })
        searchBoats({
            variables: {
                page: {
                    offset: 0,
                    limit: 100,
                },
                statusCode: 'ACT',
            },
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []) // Run once on mount - lazy queries are stable

    useEffect(() => {
        if (filterApplied) {
            fetchFleetUtilizationReport(currentPage, filterObj, perPage)
        }
    }, [currentPage, perPage, filterApplied])

    const handlePageChange = page => {
        if (filterApplied) {
            setCurrentPage(page)
        }
    }

    const handlePerRowsChange = newPerPage => {
        if (newPerPage !== perPage && filterApplied) {
            setPerPage(newPerPage)
            setCurrentPage(1)
        }
    }

    const handleFilter = () => {
        setCurrentPage(1)
        setFilterApplied(true)
        fetchFleetUtilizationReport(1, filterObj)
    }

    const handleClear = () => {
        setFilterObj({
            ...INITIAL_FILTER_STATE,
            route_ids: [],
            boat_ids: [],
        })
        setCurrentPage(1)
        setPerPage(10)
        setFilterApplied(false)
        setData([]) // Clear existing data
    }

    const fetchBatch = async (offset, limit, filterData = filterObj) => {
        try {
            const result = await getFleetUtilizationReportForExport({
                variables: {
                    page: { limit, offset },
                    fleetFilter: {
                        trip_date: {
                            start_date: filterData.trip_date.start_date,
                            end_date: filterData.trip_date.end_date,
                        },
                        route_ids: filterData.route_ids?.length > 0 ? filterData.route_ids : null,
                        boat_ids: filterData.boat_ids?.length > 0 ? filterData.boat_ids : null,
                    },
                },
            })

            if (result.data?.fleetUtilizationReport?.success && result.data.fleetUtilizationReport.code === '200') {
                return {
                    data: result.data.fleetUtilizationReport.fleet_utilization_report,
                    total: result.data.fleetUtilizationReport.total_count,
                }
            }
            throw new Error(result.data?.fleetUtilizationReport?.message || 'Failed to fetch data')
        } catch (error) {
            throw error
        }
    }

    const handleExportCSV = async () => {
        try {
            setExportLoading(true)
            const initialBatch = await fetchBatch(0, 100)
            const totalRecords = initialBatch.total

            if (totalRecords === 0) {
                handleError(t('No records found to export'))
                return
            }

            let allData = [...initialBatch.data]
            let offset = 100

            while (allData.length < totalRecords) {
                const batchData = await fetchBatch(offset, 100)
                allData = [...allData, ...batchData.data]
                offset += 100
            }

            const formattedData = allData.map(formatFleetData)
            const headers = [
                'Boat ID',
                'Boat Name',
                'Registration Number',
                'Total Trips',
                'Operational Hours',
                'Ideal Time',
            ]
            const csvContent = [
                headers.join(','),
                ...formattedData.map(row =>
                    [row.boatId, row.boatName, row.regNumber, row.totalTrips, row.operationalHours, row.idealTime].join(
                        ','
                    )
                ),
            ].join('\n')

            const blob = new Blob([csvContent], { type: 'text/csv' })
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `fleet-utilization-report-${moment().format('YYYY-MM-DD')}.csv`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            window.URL.revokeObjectURL(url)
        } catch (error) {
            handleError(error.message)
        } finally {
            setExportLoading(false)
        }
    }

    const columns = useMemo(
        () => [
            {
                name: t('Boat Name'),
                selector: row => row.boatName,
                sortable: true,
            },
            {
                name: t('Boat ID'),
                selector: row => row.boatId,
                sortable: true,
            },
            {
                name: t('Total Trips'),
                selector: row => row.totalTrips,
                sortable: true,
            },
            {
                name: t('Operational Hours'),
                selector: row => row.operationalHours,
                sortable: true,
            },
            {
                name: t('Ideal Time'),
                selector: row => row.idealTime,
                sortable: true,
            },
        ],
        [t]
    )

    const customStyles = {
        headCells: {
            style: {
                backgroundColor: '#f9fafb',
                color: '#111827',
                fontWeight: 600,
                fontSize: '0.875rem',
                paddingLeft: '1rem',
                paddingRight: '1rem',
                minWidth: 'auto',
                textAlign: i18n.language === 'ar' ? 'right' : 'left',
            },
        },
        cells: {
            style: {
                paddingLeft: '1rem',
                paddingRight: '1rem',
                minWidth: 'auto',
                textAlign: i18n.language === 'ar' ? 'right' : 'left',
            },
        },
        pagination: {
            style: {
                direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
            },
            pageButtonsStyle: {
                direction: i18n.language === 'ar' ? 'rtl' : 'ltr',
                marginLeft: i18n.language === 'ar' ? '0' : '0.5rem',
                marginRight: i18n.language === 'ar' ? '0.5rem' : '0',
            },
        },
    }

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => navigate('/')}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Home')}
                </button>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1]">
                            <BoatIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                        </div>
                        <div>
                            <span className="ms-3">{t('Fleet Performance')}</span>
                            <p className="ms-3 text-sm font-normal leading-6 text-gray-500">
                                {t('Monitor fleet performance and utilization')}
                            </p>
                        </div>
                    </h1>

                    <div className="mt-6 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-5">
                        <div className="">
                            <label className="block text-sm font-medium text-gray-700">{t('Date Range')}</label>
                            <Flatpickr
                                options={{
                                    mode: 'range',
                                    dateFormat: 'Y-m-d',
                                    defaultDate: [
                                        moment().subtract(6, 'months').format('YYYY-MM-DD'),
                                        moment().format('YYYY-MM-DD'),
                                    ],
                                }}
                                value={[filterObj.trip_date.start_date, filterObj.trip_date.end_date]}
                                onChange={dates => {
                                    setFilterObj({
                                        ...filterObj,
                                        trip_date: {
                                            start_date: dates[0] ? moment(dates[0]).format('YYYY-MM-DD') : null,
                                            end_date: dates[1] ? moment(dates[1]).format('YYYY-MM-DD') : null,
                                        },
                                    })
                                }}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">{t('Route')}</label>
                            <MultiSelect
                                placeholder={t('All')}
                                value={filterObj.route_ids || []}
                                onChange={(values) => setFilterObj({ ...filterObj, route_ids: values })}
                                options={routeOptions}
                                className="mt-1"
                            />
                        </div>

                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">{t('Boat')}</label>
                            <MultiSelect
                                placeholder={t('All')}
                                value={filterObj.boat_ids || []}
                                onChange={(values) => setFilterObj({ ...filterObj, boat_ids: values })}
                                options={boatOptions}
                                className="mt-1"
                            />
                        </div>

                        <div className="sm:col-span-2 flex items-end gap-2">
                            <button
                                onClick={handleFilter}
                                className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                            >
                                {t('Filter')}
                            </button>
                            <button
                                onClick={handleClear}
                                className="inline-flex items-center justify-center rounded-md bg-white px-6 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            >
                                {t('Clear')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <Divider className="my-4 mb-6" />

            {reportLoading ? (
                <div className="flex flex-col justify-center items-center bg-white h-96">
                    <img alt="loading" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">{t('Loading, please wait...')}</p>
                </div>
            ) : (
                <>
                    <div className="flex justify-between items-center mb-4 -mt-3">
                        <p className="font-semibold">Fleet Utilization Reports</p>
                        {data.length > 0 && (
                            <button
                                onClick={handleExportCSV}
                                disabled={exportLoading}
                                className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9] disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {exportLoading ? (
                                    <>
                                        <ArrowPathIcon className="-ml-0.5 mr-1.5 h-5 w-5 animate-spin" />
                                        {t('Exporting...')}
                                    </>
                                ) : (
                                    <>
                                        <ArrowDownTrayIcon className="-ml-0.5 mr-1.5 h-5 w-5" />
                                        {t('Export to CSV')}
                                    </>
                                )}
                            </button>
                        )}
                    </div>

                    <DataTable
                        columns={columns}
                        data={data}
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        onChangeRowsPerPage={handlePerRowsChange}
                        onChangePage={handlePageChange}
                        customStyles={customStyles}
                        paginationRowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                        paginationPerPage={perPage}
                        noDataComponent={
                            <div className="text-center py-24 bg-gray-50 mt-0 rounded-lg w-full">
                                <svg
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                    className="mx-auto h-12 w-12 text-gray-400"
                                >
                                    <path
                                        d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                                        strokeWidth={2}
                                        vectorEffect="non-scaling-stroke"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No records found!')}</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    {t('Please add or modify your search criteria and try again.')}
                                </p>
                            </div>
                        }
                    />
                </>
            )}

            {showErrDialog && (
                <ErrorDialog
                    status={true}
                    title={t('Error')}
                    message={dialogMessage}
                    onClose={() => setShowErrDialog(false)}
                />
            )}
        </>
    )
}
